import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { Card, Badge, Button, Table, Input, Modal, ConfidenceBadge } from '@/components/ui'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { formatDate, formatListTimestamp } from '@/utils/format-date'
import type {
  AdminOrganizationDetailResponse,
  AdminUserResponse,
  CanaryWithStats,
  DetectionResponse,
} from '@spoofio/shared/types'

type BadgeVariant = 'success' | 'danger' | 'warning' | 'primary' | 'gray'

const STATUS_BADGE_MAP: Record<string, { label: string; variant: BadgeVariant }> = {
  trial: { label: 'Trial', variant: 'primary' },
  trial_expired: { label: 'Trial Expired', variant: 'warning' },
  active: { label: 'Active', variant: 'success' },
  past_due: { label: 'Overdue', variant: 'danger' },
  canceled: { label: 'Canceled', variant: 'gray' },
  suspended: { label: 'Suspended', variant: 'warning' },
  inactive: { label: 'Inactive', variant: 'gray' },
}

type Tab = 'overview' | 'members' | 'canaries' | 'detections'

export function AdminOrganizationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { startImpersonation } = useImpersonation()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [confirmAction, setConfirmAction] = useState<string | null>(null)
  const [trialDays, setTrialDays] = useState('14')
  const [suspendReason, setSuspendReason] = useState('')
  const [impersonateError, setImpersonateError] = useState<string | null>(null)
  const [deleteConfirmName, setDeleteConfirmName] = useState('')

  const orgId = id ?? ''

  const { data: org, isLoading, error } = useQuery({
    queryKey: ['admin-organization', orgId],
    queryFn: () => api.adminGetOrganization(orgId),
    enabled: orgId.length > 0,
  })

  const invalidateOrg = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-organization', orgId] })
    void queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
  }

  const extendTrial = useMutation({
    mutationFn: () => api.adminExtendTrial(orgId, parseInt(trialDays, 10)),
    onSuccess: () => { invalidateOrg(); setConfirmAction(null) },
  })

  const suspend = useMutation({
    mutationFn: () => api.adminSuspendOrganization(orgId, suspendReason || undefined),
    onSuccess: () => { invalidateOrg(); setConfirmAction(null); setSuspendReason('') },
  })

  const reactivate = useMutation({
    mutationFn: () => api.adminReactivateOrganization(orgId),
    onSuccess: () => { invalidateOrg(); setConfirmAction(null) },
  })

  const cancel = useMutation({
    mutationFn: () => api.adminCancelOrganization(orgId),
    onSuccess: () => { invalidateOrg(); setConfirmAction(null) },
  })

  const deleteOrg = useMutation({
    mutationFn: () => api.adminDeleteOrganization(orgId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
      navigate('/admin/organizations')
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-64" />
        <div className="h-64 bg-gray-100 rounded" />
      </div>
    )
  }

  if (error || !org) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-600">Failed to load organization</p>
        <Link to="/admin/organizations" className="text-primary-600 text-sm mt-2 inline-block">
          Back to Organizations
        </Link>
      </div>
    )
  }

  const statusBadge = STATUS_BADGE_MAP[org.displayStatus] ?? {
    label: org.displayStatus,
    variant: 'gray' as BadgeVariant,
  }

  const isPlatformOrg = org.isPlatform

  const showExtendTrial = org.displayStatus === 'trial' || org.displayStatus === 'trial_expired'
  const showSuspend = org.displayStatus !== 'suspended' && org.displayStatus !== 'canceled' && org.displayStatus !== 'inactive'
  const showReactivate = org.displayStatus === 'suspended' || org.displayStatus === 'canceled'
  const showCancel = org.displayStatus !== 'canceled' && org.displayStatus !== 'inactive'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/organizations" className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{org.name}</h1>
          <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
        </div>
        <div className="flex gap-2">
          {showExtendTrial && (
            <Button variant="secondary" size="sm" onClick={() => { setConfirmAction('extend') }}>
              Extend Trial
            </Button>
          )}
          {showSuspend && (
            <Button variant="danger" size="sm" onClick={() => { setConfirmAction('suspend') }}>
              Suspend
            </Button>
          )}
          {showReactivate && (
            <Button variant="primary" size="sm" onClick={() => { setConfirmAction('reactivate') }}>
              Reactivate
            </Button>
          )}
          {showCancel && (
            <Button variant="danger" size="sm" onClick={() => { setConfirmAction('cancel') }}>
              Cancel
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => { setConfirmAction('act-as') }}>
            Act As
          </Button>
          {!isPlatformOrg && (
            <Button variant="danger" size="sm" onClick={() => { setConfirmAction('delete') }}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(['overview', 'members', 'canaries', 'detections'] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab) }}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab org={org} orgId={orgId} />}
      {activeTab === 'members' && <MembersTab orgId={orgId} />}
      {activeTab === 'canaries' && <CanariesTab orgId={orgId} />}
      {activeTab === 'detections' && <DetectionsTab orgId={orgId} />}

      {/* Confirmation Modals */}
      <Modal
        isOpen={confirmAction === 'extend'}
        onClose={() => { setConfirmAction(null) }}
        title="Extend Trial"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmAction(null) }}>Cancel</Button>
            <Button
              isLoading={extendTrial.isPending}
              onClick={() => { extendTrial.mutate() }}
            >
              Extend
            </Button>
          </div>
        }
      >
        <Input
          label="Days to extend"
          type="number"
          min="1"
          max="365"
          value={trialDays}
          onChange={(e) => { setTrialDays(e.target.value) }}
        />
      </Modal>

      <Modal
        isOpen={confirmAction === 'suspend'}
        onClose={() => { setConfirmAction(null) }}
        title="Suspend Organization"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmAction(null) }}>Cancel</Button>
            <Button
              variant="danger"
              isLoading={suspend.isPending}
              onClick={() => { suspend.mutate() }}
            >
              Suspend
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-4">
          This will immediately suspend the organization. All detection processing will be paused.
        </p>
        <Input
          label="Reason (optional)"
          value={suspendReason}
          onChange={(e) => { setSuspendReason(e.target.value) }}
          placeholder="e.g. Payment dispute, TOS violation"
        />
      </Modal>

      <Modal
        isOpen={confirmAction === 'reactivate'}
        onClose={() => { setConfirmAction(null) }}
        title="Reactivate Organization"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmAction(null) }}>Cancel</Button>
            <Button
              isLoading={reactivate.isPending}
              onClick={() => { reactivate.mutate() }}
            >
              Reactivate
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          This will reactivate the organization and resume all services.
        </p>
      </Modal>

      <Modal
        isOpen={confirmAction === 'cancel'}
        onClose={() => { setConfirmAction(null) }}
        title="Cancel Organization"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmAction(null) }}>Go Back</Button>
            <Button
              variant="danger"
              isLoading={cancel.isPending}
              onClick={() => { cancel.mutate() }}
            >
              Cancel Organization
            </Button>
          </div>
        }
      >
        <p className="text-sm text-danger-600 font-medium">
          This action cannot be easily undone. The organization will be marked as canceled.
        </p>
      </Modal>

      <Modal
        isOpen={confirmAction === 'act-as'}
        onClose={() => { setConfirmAction(null); setImpersonateError(null) }}
        title="Act As Organization"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmAction(null); setImpersonateError(null) }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setImpersonateError(null)
                startImpersonation(orgId)
                  .then(() => { setConfirmAction(null); navigate('/') })
                  .catch(() => { setImpersonateError('Failed to start impersonation') })
              }}
            >
              Start Impersonation
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          You will view the platform as <strong>{org.name}</strong> in read-only mode. This session will expire in 1 hour.
        </p>
        {impersonateError && (
          <p className="text-sm text-danger-600 mt-2">{impersonateError}</p>
        )}
      </Modal>

      <Modal
        isOpen={confirmAction === 'delete'}
        onClose={() => { setConfirmAction(null); setDeleteConfirmName('') }}
        title="Delete Organization"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmAction(null); setDeleteConfirmName('') }}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={deleteOrg.isPending}
              disabled={deleteConfirmName !== org.name}
              onClick={() => { deleteOrg.mutate() }}
            >
              Delete Organization
            </Button>
          </div>
        }
      >
        <p className="text-sm text-danger-600 font-medium mb-4">
          This action is irreversible. All canaries, detections, alerts, and members will be permanently deleted.
        </p>
        <Input
          label={`Type "${org.name}" to confirm`}
          value={deleteConfirmName}
          onChange={(e) => { setDeleteConfirmName(e.target.value) }}
          placeholder={org.name}
        />
        {deleteOrg.error && (
          <p className="text-sm text-danger-600 mt-2">
            {deleteOrg.error instanceof Error ? deleteOrg.error.message : 'Failed to delete organization'}
          </p>
        )}
      </Modal>
    </div>
  )
}

function OverviewTab({ org, orgId }: { org: AdminOrganizationDetailResponse; orgId: string }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(org.name)
  const [slug, setSlug] = useState(org.slug)

  const updateOrg = useMutation({
    mutationFn: () => api.adminUpdateOrganization(orgId, { name, slug }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-organization', orgId] })
      setEditing(false)
    },
  })

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card title="Organization Info">
        {editing ? (
          <div className="space-y-4">
            <Input label="Name" name="org-name" value={name} onChange={(e) => { setName(e.target.value) }} />
            <Input label="Slug" name="org-slug" value={slug} onChange={(e) => { setSlug(e.target.value) }} />
            <div className="flex gap-2">
              <Button size="sm" isLoading={updateOrg.isPending} onClick={() => { updateOrg.mutate() }}>
                Save
              </Button>
              <Button size="sm" variant="secondary" onClick={() => { setEditing(false); setName(org.name); setSlug(org.slug) }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <dl className="space-y-3">
            <InfoRow label="Name" value={org.name} />
            <InfoRow label="Slug" value={org.slug} />
            <InfoRow label="Plan" value={org.planType} />
            <InfoRow label="Members" value={String(org.memberCount)} />
            <InfoRow label="Canaries" value={String(org.canaryCount)} />
            <InfoRow label="Created" value={formatDate(org.createdAt)} />
            {org.trialEndsAt && <InfoRow label="Trial Ends" value={formatDate(org.trialEndsAt)} />}
            {org.suspendedAt && <InfoRow label="Suspended" value={formatDate(org.suspendedAt)} />}
            {org.suspendedReason && <InfoRow label="Suspend Reason" value={org.suspendedReason} />}
            {org.canceledAt && <InfoRow label="Canceled" value={formatDate(org.canceledAt)} />}
            <div className="pt-2">
              <Button size="sm" variant="secondary" onClick={() => { setEditing(true) }}>Edit</Button>
            </div>
          </dl>
        )}
      </Card>

      <Card title="Recent Detections">
        {org.recentDetections.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No recent detections</p>
        ) : (
          <Table
            columns={[
              {
                key: 'fakeFqdn',
                header: 'Fake Domain',
                render: (d: DetectionResponse) => (
                  <span className="font-mono text-sm">{d.fakeFqdn}</span>
                ),
              },
              {
                key: 'confidenceScore',
                header: 'Confidence',
                render: (d: DetectionResponse) => <ConfidenceBadge score={d.confidenceScore} />,
              },
              {
                key: 'detectedAt',
                header: 'Time',
                render: (d: DetectionResponse) => formatListTimestamp(d.detectedAt),
              },
            ]}
            data={org.recentDetections}
            keyExtractor={(d) => d.id}
            emptyMessage="No detections"
          />
        )}
      </Card>
    </div>
  )
}

function MembersTab({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addRole, setAddRole] = useState('member')
  const [confirmRemove, setConfirmRemove] = useState<AdminUserResponse | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-org-users', orgId],
    queryFn: () => api.adminListOrgUsers(orgId),
  })

  const invalidateMembers = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-org-users', orgId] })
    void queryClient.invalidateQueries({ queryKey: ['admin-organization', orgId] })
  }

  const addMember = useMutation({
    mutationFn: () => api.adminAddMember(orgId, { email: addEmail, role: addRole }),
    onSuccess: () => {
      invalidateMembers()
      setShowAddModal(false)
      setAddEmail('')
      setAddRole('member')
    },
  })

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.adminRemoveMember(orgId, userId),
    onSuccess: () => {
      invalidateMembers()
      setConfirmRemove(null)
    },
  })

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.adminUpdateMemberRole(orgId, userId, { role }),
    onSuccess: () => { invalidateMembers() },
  })

  const columns = [
    {
      key: 'email',
      header: 'Email',
      render: (u: AdminUserResponse) => u.email,
    },
    {
      key: 'name',
      header: 'Name',
      render: (u: AdminUserResponse) =>
        [u.firstName, u.lastName].filter(Boolean).join(' ') || '-',
    },
    {
      key: 'role',
      header: 'Role',
      render: (u: AdminUserResponse) => {
        const role = u.organizations.find((o) => o.id === orgId)?.role ?? 'member'
        return (
          <select
            value={role}
            onChange={(e) => { updateRole.mutate({ userId: u.id, role: e.target.value }) }}
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
          >
            <option value="owner">owner</option>
            <option value="admin">admin</option>
            <option value="member">member</option>
          </select>
        )
      },
    },
    {
      key: 'mfa',
      header: 'MFA',
      render: (u: AdminUserResponse) => (
        <Badge variant={u.mfaEnabled ? 'success' : 'gray'}>
          {u.mfaEnabled ? 'Enabled' : 'Off'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (u: AdminUserResponse) => (
        <Button
          variant="danger"
          size="sm"
          onClick={() => { setConfirmRemove(u) }}
        >
          Remove
        </Button>
      ),
    },
  ]

  return (
    <>
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Members</h3>
          <Button size="sm" onClick={() => { setShowAddModal(true) }}>
            Add Member
          </Button>
        </div>
        <Table
          columns={columns}
          data={data?.data ?? []}
          keyExtractor={(u) => u.id}
          isLoading={isLoading}
          emptyMessage="No members found"
        />
      </Card>

      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setAddEmail(''); setAddRole('member') }}
        title="Add Member"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowAddModal(false) }}>Cancel</Button>
            <Button
              isLoading={addMember.isPending}
              disabled={!addEmail}
              onClick={() => { addMember.mutate() }}
            >
              Add
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={addEmail}
            onChange={(e) => { setAddEmail(e.target.value) }}
            placeholder="user@example.com"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={addRole}
              onChange={(e) => { setAddRole(e.target.value) }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="member">member</option>
              <option value="admin">admin</option>
              <option value="owner">owner</option>
            </select>
          </div>
          {addMember.error && (
            <p className="text-sm text-danger-600">
              {addMember.error instanceof Error ? addMember.error.message : 'Failed to add member'}
            </p>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={confirmRemove !== null}
        onClose={() => { setConfirmRemove(null) }}
        title="Remove Member"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmRemove(null) }}>Cancel</Button>
            <Button
              variant="danger"
              isLoading={removeMember.isPending}
              onClick={() => { if (confirmRemove) removeMember.mutate(confirmRemove.id) }}
            >
              Remove
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to remove <strong>{confirmRemove?.email}</strong> from this organization?
        </p>
        {removeMember.error && (
          <p className="text-sm text-danger-600 mt-2">
            {removeMember.error instanceof Error ? removeMember.error.message : 'Failed to remove member'}
          </p>
        )}
      </Modal>
    </>
  )
}

function CanariesTab({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editCanary, setEditCanary] = useState<CanaryWithStats | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<CanaryWithStats | null>(null)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createDomains, setCreateDomains] = useState('')
  const [createScreenshot, setCreateScreenshot] = useState(false)
  const [createThreshold, setCreateThreshold] = useState(70)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editScreenshot, setEditScreenshot] = useState(false)
  const [editThreshold, setEditThreshold] = useState(70)
  const [editIsActive, setEditIsActive] = useState(true)
  const [newDomain, setNewDomain] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['admin-org-canaries', orgId],
    queryFn: () => api.adminListOrgCanaries(orgId),
  })

  const invalidateCanaries = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-org-canaries', orgId] })
    void queryClient.invalidateQueries({ queryKey: ['admin-organization', orgId] })
    void queryClient.invalidateQueries({ queryKey: ['admin-canaries'] })
  }

  const createCanary = useMutation({
    mutationFn: () =>
      api.adminCreateCanary({
        organizationId: orgId,
        name: createName,
        description: createDescription || undefined,
        authorizedDomains: createDomains
          .split(/[,\n]/)
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        screenshotEnabled: createScreenshot,
        confidenceThreshold: createThreshold,
      }),
    onSuccess: () => {
      invalidateCanaries()
      setShowCreateModal(false)
      setCreateName('')
      setCreateDescription('')
      setCreateDomains('')
      setCreateScreenshot(false)
      setCreateThreshold(70)
    },
  })

  const updateCanary = useMutation({
    mutationFn: () => {
      if (!editCanary) throw new Error('No canary selected')
      return api.adminUpdateCanary(editCanary.id, {
        name: editName,
        description: editDescription || undefined,
        screenshotEnabled: editScreenshot,
        confidenceThreshold: editThreshold,
        isActive: editIsActive,
      })
    },
    onSuccess: () => {
      invalidateCanaries()
      setEditCanary(null)
    },
  })

  const deleteCanaryMut = useMutation({
    mutationFn: (id: string) => api.adminDeleteCanary(id),
    onSuccess: () => {
      invalidateCanaries()
      setConfirmDelete(null)
    },
  })

  const refreshEditCanary = async () => {
    if (!editCanary) return
    try {
      const res = await api.adminListOrgCanaries(orgId)
      const updated = res.data.find((c: CanaryWithStats) => c.id === editCanary.id)
      if (updated) setEditCanary(updated)
    } catch { /* ignore */ }
  }

  const addDomain = useMutation({
    mutationFn: () => {
      if (!editCanary) throw new Error('No canary selected')
      return api.adminAddAuthorizedDomain(editCanary.id, newDomain.trim().toLowerCase())
    },
    onSuccess: () => {
      invalidateCanaries()
      setNewDomain('')
      void refreshEditCanary()
    },
  })

  const removeDomain = useMutation({
    mutationFn: (domainId: string) => api.adminRemoveAuthorizedDomain(domainId),
    onSuccess: () => {
      invalidateCanaries()
      void refreshEditCanary()
    },
  })

  const openEdit = (c: CanaryWithStats) => {
    setEditCanary(c)
    setEditName(c.name)
    setEditDescription(c.description ?? '')
    setEditScreenshot(c.screenshotEnabled)
    setEditThreshold(c.confidenceThreshold)
    setEditIsActive(c.isActive)
    setNewDomain('')
  }

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (c: CanaryWithStats) => c.name,
    },
    {
      key: 'token',
      header: 'Token',
      render: (c: CanaryWithStats) => (
        <span className="font-mono text-xs text-gray-500">
          {c.token.slice(0, 12)}...
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (c: CanaryWithStats) => (
        <Badge variant={c.isActive ? 'success' : 'gray'}>
          {c.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'detections24h',
      header: 'Detections (24h)',
      render: (c: CanaryWithStats) => c.detectionCount24h,
    },
    {
      key: 'actions',
      header: '',
      render: (c: CanaryWithStats) => (
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => { openEdit(c) }}>
            Edit
          </Button>
          <Button variant="danger" size="sm" onClick={() => { setConfirmDelete(c) }}>
            Delete
          </Button>
        </div>
      ),
    },
  ]

  return (
    <>
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Canaries</h3>
          <Button size="sm" onClick={() => { setShowCreateModal(true) }}>
            Create Canary
          </Button>
        </div>
        <Table
          columns={columns}
          data={data?.data ?? []}
          keyExtractor={(c) => c.id}
          isLoading={isLoading}
          emptyMessage="No canaries found"
        />
      </Card>

      {/* Create Canary Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false) }}
        title="Create Canary"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowCreateModal(false) }}>Cancel</Button>
            <Button
              isLoading={createCanary.isPending}
              disabled={!createName || !createDomains.trim()}
              onClick={() => { createCanary.mutate() }}
            >
              Create
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input label="Name" value={createName} onChange={(e) => { setCreateName(e.target.value) }} placeholder="e.g. Donation Page" />
          <Input label="Description (optional)" value={createDescription} onChange={(e) => { setCreateDescription(e.target.value) }} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Domains (comma or newline separated)</label>
            <textarea value={createDomains} onChange={(e) => { setCreateDomains(e.target.value) }} placeholder="example.com, cdn.example.com" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" rows={3} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="org-create-screenshot" checked={createScreenshot} onChange={(e) => { setCreateScreenshot(e.target.checked) }} className="rounded border-gray-300" />
            <label htmlFor="org-create-screenshot" className="text-sm text-gray-700">Enable screenshots</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Threshold: {createThreshold}</label>
            <input type="range" min="0" max="100" value={createThreshold} onChange={(e) => { setCreateThreshold(Number(e.target.value)) }} className="w-full" />
          </div>
          {createCanary.error && (
            <p className="text-sm text-danger-600">{createCanary.error instanceof Error ? createCanary.error.message : 'Failed to create canary'}</p>
          )}
        </div>
      </Modal>

      {/* Edit Canary Modal */}
      <Modal
        isOpen={editCanary !== null}
        onClose={() => { setEditCanary(null) }}
        title="Edit Canary"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setEditCanary(null) }}>Cancel</Button>
            <Button isLoading={updateCanary.isPending} onClick={() => { updateCanary.mutate() }}>
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input label="Name" value={editName} onChange={(e) => { setEditName(e.target.value) }} />
          <Input label="Description" value={editDescription} onChange={(e) => { setEditDescription(e.target.value) }} />
          <div className="flex items-center gap-2">
            <input type="checkbox" id="edit-screenshot" checked={editScreenshot} onChange={(e) => { setEditScreenshot(e.target.checked) }} className="rounded border-gray-300" />
            <label htmlFor="edit-screenshot" className="text-sm text-gray-700">Enable screenshots</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Threshold: {editThreshold}</label>
            <input type="range" min="0" max="100" value={editThreshold} onChange={(e) => { setEditThreshold(Number(e.target.value)) }} className="w-full" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="edit-active" checked={editIsActive} onChange={(e) => { setEditIsActive(e.target.checked) }} className="rounded border-gray-300" />
            <label htmlFor="edit-active" className="text-sm text-gray-700">Active</label>
          </div>

          {/* Domain Management */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Authorized Domains</h4>
            {editCanary?.authorizedDomains.map((d) => (
              <div key={d.id} className="flex items-center justify-between py-1">
                <span className="text-sm font-mono text-gray-700">{d.fqdn}</span>
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={removeDomain.isPending}
                  onClick={() => { removeDomain.mutate(d.id) }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <Input
                value={newDomain}
                onChange={(e) => { setNewDomain(e.target.value) }}
                placeholder="new-domain.com"
                className="flex-1"
              />
              <Button
                size="sm"
                disabled={!newDomain.trim()}
                isLoading={addDomain.isPending}
                onClick={() => { addDomain.mutate() }}
              >
                Add
              </Button>
            </div>
            {addDomain.error && (
              <p className="text-sm text-danger-600 mt-1">{addDomain.error instanceof Error ? addDomain.error.message : 'Failed to add domain'}</p>
            )}
          </div>

          {updateCanary.error && (
            <p className="text-sm text-danger-600">{updateCanary.error instanceof Error ? updateCanary.error.message : 'Failed to update canary'}</p>
          )}
        </div>
      </Modal>

      {/* Delete Canary Confirmation */}
      <Modal
        isOpen={confirmDelete !== null}
        onClose={() => { setConfirmDelete(null) }}
        title="Delete Canary"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setConfirmDelete(null) }}>Cancel</Button>
            <Button
              variant="danger"
              isLoading={deleteCanaryMut.isPending}
              onClick={() => { if (confirmDelete) deleteCanaryMut.mutate(confirmDelete.id) }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-sm text-danger-600 font-medium">
          This will permanently delete canary <strong>{confirmDelete?.name}</strong> and all its detections and alerts.
        </p>
        {deleteCanaryMut.error && (
          <p className="text-sm text-danger-600 mt-2">{deleteCanaryMut.error instanceof Error ? deleteCanaryMut.error.message : 'Failed to delete canary'}</p>
        )}
      </Modal>
    </>
  )
}

function DetectionsTab({ orgId }: { orgId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-org-detections', orgId],
    queryFn: () => api.adminListOrgDetections(orgId, { limit: 25 }),
  })

  const columns = [
    {
      key: 'fakeFqdn',
      header: 'Fake Domain',
      render: (d: DetectionResponse) => (
        <span className="font-mono text-sm">{d.fakeFqdn}</span>
      ),
    },
    {
      key: 'canaryName',
      header: 'Canary',
      render: (d: DetectionResponse) => d.canaryName,
    },
    {
      key: 'confidenceScore',
      header: 'Confidence',
      render: (d: DetectionResponse) => <ConfidenceBadge score={d.confidenceScore} />,
    },
    {
      key: 'detectedAt',
      header: 'Time',
      render: (d: DetectionResponse) => formatListTimestamp(d.detectedAt),
    },
  ]

  return (
    <Card>
      <Table
        columns={columns}
        data={data?.data ?? []}
        keyExtractor={(d) => d.id}
        isLoading={isLoading}
        emptyMessage="No detections found"
      />
    </Card>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{value}</dd>
    </div>
  )
}

